import React from "react";
import {
  onPortalSessionExpired,
  onPortalPrivacyConsentRequired,
  portalApi,
  refreshPortalSession,
  setPortalAccessToken,
} from "../api/client";
import type {
  ApiEnvelope,
  AuthenticationResult,
  PortalMe,
  SupplierSelection,
  PrivacyConsentPending,
} from "../api/types";

type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "selecting" | "consenting";

interface AuthContextValue {
  status: AuthStatus;
  me: PortalMe | null;
  selection: SupplierSelection | null;
  privacyConsent: PrivacyConsentPending | null;
  acceptPrivacy(): Promise<void>;
  declinePrivacy(): Promise<void>;
  login(email: string, password: string): Promise<AuthenticationResult>;
  selectSupplier(supplierUserId: number): Promise<void>;
  acceptInvite(token: string, password?: string): Promise<AuthenticationResult>;
  resetPassword(token: string, password: string): Promise<void>;
  switchSupplier(supplierUserId: number): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);
let bootstrapPromise: Promise<void> | null = null;

export function AuthProvider({ children }: React.PropsWithChildren) {
  const [status, setStatus] = React.useState<AuthStatus>("loading");
  const [me, setMe] = React.useState<PortalMe | null>(null);
  const [selection, setSelection] = React.useState<SupplierSelection | null>(
    null
  );
  const [privacyConsent, setPrivacyConsent] = React.useState<PrivacyConsentPending | null>(null);

  const clearSession = React.useCallback(() => {
    setPortalAccessToken(null);
    setMe(null);
    setSelection(null);
    setPrivacyConsent(null);
    setStatus("unauthenticated");
  }, []);

  const loadMe = React.useCallback(async () => {
    const response = await portalApi.get<ApiEnvelope<PortalMe>>("/portal/me");
    setMe(response.data.data);
    setSelection(null);
    setStatus("authenticated");
  }, []);

  React.useEffect(() => {
    onPortalSessionExpired(clearSession);
    onPortalPrivacyConsentRequired((pending) => { setPrivacyConsent(pending); setStatus('consenting'); });

    if (!bootstrapPromise) {
      bootstrapPromise = refreshPortalSession()
        .then((result) => 'requiresPrivacyConsent' in result ? undefined : loadMe())
        .catch(() => clearSession());
    } else {
      bootstrapPromise.then(() => status === 'consenting' ? undefined : loadMe()).catch(() => clearSession());
    }

    return () => { onPortalSessionExpired(null); onPortalPrivacyConsentRequired(null); };
  }, [clearSession, loadMe]);

  const applyAuthentication = React.useCallback(
    async (result: AuthenticationResult) => {
      if ('requiresPrivacyConsent' in result) { setPortalAccessToken(null); setMe(null); setSelection(null); setPrivacyConsent(result); setStatus('consenting'); return; }
      if (result.requiresSupplierSelection) {
        setPortalAccessToken(null);
        setMe(null);
        setSelection(result);
        setStatus("selecting");
        return;
      }
      setPortalAccessToken(result.accessToken);
      await loadMe();
    },
    [loadMe]
  );

  const acceptPrivacy = React.useCallback(async () => { if (!privacyConsent) return; const response = await portalApi.post<ApiEnvelope<AuthenticationResult>>('/portal/auth/privacy-consent/accept', {}, { headers: { Authorization: `Bearer ${privacyConsent.consentToken}` }, skipAuth: true, skipRefresh: true } as never); setPrivacyConsent(null); await applyAuthentication(response.data.data); }, [privacyConsent, applyAuthentication]);
  const declinePrivacy = React.useCallback(async () => { if (!privacyConsent) return; try { await portalApi.post('/portal/auth/privacy-consent/decline', {}, { headers: { Authorization: `Bearer ${privacyConsent.consentToken}` }, skipAuth: true, skipRefresh: true } as never); } finally { clearSession(); } }, [privacyConsent, clearSession]);

  const login = React.useCallback(
    async (email: string, password: string) => {
      const response = await portalApi.post<ApiEnvelope<AuthenticationResult>>(
        "/portal/auth/login",
        { email, password },
        { skipAuth: true, skipRefresh: true } as never
      );
      await applyAuthentication(response.data.data);
      return response.data.data;
    },
    [applyAuthentication]
  );

  const selectSupplier = React.useCallback(
    async (supplierUserId: number) => {
      if (!selection) throw new Error("Sessão de seleção expirada.");
      const response = await portalApi.post<ApiEnvelope<AuthenticationResult>>(
        "/portal/auth/select-supplier",
        { supplierUserId },
        {
          headers: { Authorization: `Bearer ${selection.selectionToken}` },
          skipAuth: true,
          skipRefresh: true,
        } as never
      );
      await applyAuthentication(response.data.data);
    },
    [applyAuthentication, selection]
  );

  const acceptInvite = React.useCallback(
    async (token: string, password?: string) => {
      const response = await portalApi.post<ApiEnvelope<AuthenticationResult>>(
        `/portal/invite/${encodeURIComponent(token)}/accept`,
        password ? { password } : {},
        { skipAuth: true, skipRefresh: true } as never
      );
      await applyAuthentication(response.data.data);
      return response.data.data;
    },
    [applyAuthentication]
  );

  const resetPassword = React.useCallback(
    async (token: string, password: string) => {
      await portalApi.post(
        `/portal/auth/reset/${encodeURIComponent(token)}`,
        { password },
        { skipAuth: true, skipRefresh: true } as never
      );
      clearSession();
    },
    [clearSession]
  );

  const switchSupplier = React.useCallback(
    async (supplierUserId: number) => {
      if (!me?.selectionToken) {
        throw new Error("Não há outra empresa disponível para esta sessão.");
      }
      const response = await portalApi.post<ApiEnvelope<AuthenticationResult>>(
        "/portal/auth/select-supplier",
        { supplierUserId },
        {
          headers: { Authorization: `Bearer ${me.selectionToken}` },
          skipAuth: true,
          skipRefresh: true,
        } as never
      );
      await applyAuthentication(response.data.data);
    },
    [applyAuthentication, me]
  );

  const logout = React.useCallback(async () => {
    try {
      await portalApi.post("/portal/auth/logout", undefined, {
        skipRefresh: true,
      } as never);
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      status,
      me,
      selection,
      privacyConsent,
      acceptPrivacy,
      declinePrivacy,
      login,
      selectSupplier,
      acceptInvite,
      resetPassword,
      switchSupplier,
      logout,
    }),
    [
      status,
      me,
      selection,
      privacyConsent,
      acceptPrivacy,
      declinePrivacy,
      login,
      selectSupplier,
      acceptInvite,
      resetPassword,
      switchSupplier,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context)
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return context;
}
